import React, { useEffect, useState } from 'react'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import '../../Employee/screens/CreateImprovementPlan.css'
import { FaSave, FaSpinner } from 'react-icons/fa'
import { toast } from 'react-toastify'
import axios from 'axios'
import Cookies from 'js-cookie'
import { useParams, useNavigate } from 'react-router-dom'
import API_BASE_URL from '../../../config/api'

const COOEditIP = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    strengths: '',
    weaknesses: '',
    employee_comment: '',
    training_needs: '',
    employee_signature: null
  })
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [errors, setErrors] = useState({})
  const baseUrl = API_BASE_URL

  useEffect(() => {
    const fetchPlan = async () => {
      setInitialLoading(true)
      try {
        const token = Cookies.get('token')
        const response = await axios.get(`${baseUrl}/api/improvement-plans/${id}/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        const data = response.data
        setFormData({
          strengths: data?.strengths || '',
          weaknesses: data?.weaknesses || '',
          employee_comment: data?.employee_comment || '',
          training_needs: data?.training_needs || '',
          employee_signature: null
        })
      } catch (error) {
        console.error('Error fetching improvement plan:', error)
        toast.error(error.response?.data?.detail || 'Failed to load improvement plan')
      } finally {
        setInitialLoading(false)
      }
    }

    if (id) {
      fetchPlan()
    }
  }, [id])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.strengths.trim()) {
      newErrors.strengths = 'Strengths are required'
    }
    if (!formData.weaknesses.trim()) {
      newErrors.weaknesses = 'Weaknesses are required'
    }
    if (!formData.employee_comment.trim()) {
      newErrors.employee_comment = 'Employee comment is required'
    }
    if (!formData.training_needs.trim()) {
      newErrors.training_needs = 'Training needs are required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fill in all required fields')
      return
    }

    setLoading(true)
    try {
      const token = Cookies.get('token')

      const submitData = new FormData()
      submitData.append('strengths', formData.strengths)
      submitData.append('weaknesses', formData.weaknesses)
      submitData.append('employee_comment', formData.employee_comment)
      submitData.append('training_needs', formData.training_needs)

      await axios.put(
        `${baseUrl}/api/improvement-plans/${id}/`,
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      )

      toast.success('Improvement plan updated successfully!')
      navigate(-1)
    } catch (error) {
      console.error('Error updating improvement plan:', error)
      toast.error(error.response?.data?.detail || 'Failed to update improvement plan. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employee-goals-container">
        <div className="page-header">
          <div className="header-content">
            <div className="page-title">
              <h3>Edit Improvement Plan</h3>
              <p>Update the details for this improvement plan</p>
            </div>
          </div>
        </div>

        <div style={{padding: '20px'}} className="formcontainer">
          {initialLoading ? (
            <div className="loading-state">
              <FaSpinner className="spinner" /> Loading...
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="improvement-plan-form">
              <div className="form-section">
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="strengths">Strengths *</label>
                    <textarea
                      id="strengths"
                      name="strengths"
                      value={formData.strengths}
                      onChange={handleInputChange}
                      placeholder="Describe your key strengths and areas where you excel..."
                      className={errors.strengths ? 'error' : ''}
                      rows={4}
                      cols={1}
                    />
                    {errors.strengths && <span className="error-message">{errors.strengths}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="weaknesses">Weaknesses *</label>
                    <textarea
                      id="weaknesses"
                      name="weaknesses"
                      value={formData.weaknesses}
                      onChange={handleInputChange}
                      placeholder="Identify areas for improvement and development..."
                      className={errors.weaknesses ? 'error' : ''}
                      rows={4}
                      cols={1}
                    />
                    {errors.weaknesses && <span className="error-message">{errors.weaknesses}</span>}
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="employee_comment">Comment *</label>
                    <textarea
                      id="employee_comment"
                      name="employee_comment"
                      value={formData.employee_comment}
                      onChange={handleInputChange}
                      placeholder="Share your thoughts, concerns, or suggestions about your performance and development..."
                      className={errors.employee_comment ? 'error' : ''}
                      rows={4}
                      cols={1}
                    />
                    {errors.employee_comment && <span className="error-message">{errors.employee_comment}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="training_needs">Training Needs *</label>
                    <textarea
                      id="training_needs"
                      name="training_needs"
                      value={formData.training_needs}
                      onChange={handleInputChange}
                      placeholder="Specify the training, courses, or development activities you need..."
                      className={errors.training_needs ? 'error' : ''}
                      rows={4}
                      cols={1}
                    />
                    {errors.training_needs && <span className="error-message">{errors.training_needs}</span>}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <FaSpinner className="spinner" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Update Improvement Plan
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  )
}

export default COOEditIP